import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProviderProfile } from '@/hooks/useProviderProfile';
import { useProviderUpdate } from '@/hooks/useProviderUpdate';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { UpdateProviderLocation } from '@/components/UpdateProviderLocation';
import { EditBusinessInfoModal } from '@/components/provider/EditBusinessInfoModal';
import { EditServicesModal } from '@/components/provider/EditServicesModal';
import { EditBusinessHoursModal } from '@/components/provider/EditBusinessHoursModal';
import { ManagePhotosModal } from '@/components/provider/ManagePhotosModal';
import { ProviderVerificationAlert } from '@/components/ProviderVerificationAlert';
import { ProviderGallery } from '@/components/provider/ProviderGallery';
import { Loader2, MapPin, Phone, Mail, Globe, Calendar, Image, CheckCircle, XCircle, AlertCircle, Eye, MessageSquare, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function ProviderDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: provider, isLoading } = useProviderProfile(user?.id);
  const { updateProfile, isUpdating } = useProviderUpdate(user?.id);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showBusinessInfoModal, setShowBusinessInfoModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showHoursModal, setShowHoursModal] = useState(false);
  const [showPhotosModal, setShowPhotosModal] = useState(false);

  const handleLocationUpdate = (locationData: any) => {
    updateProfile(locationData);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">No provider profile found</p>
            <Button onClick={() => navigate('/register-provider')}>
              Register as Provider
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadge = () => {
    if (provider.verified) {
      return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Verified</Badge>;
    }
    // Check subscription_status instead as it's part of the ServiceProvider type
    if ((provider as any).subscription_status === 'active' || (provider as any).provider_payment_status === 'active') {
      return <Badge className="bg-yellow-500"><AlertCircle className="w-3 h-3 mr-1" />Pending Verification</Badge>;
    }
    return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
  };

  const getVerificationStatus = (): 'verified' | 'pending' | 'inactive' | 'rejected' => {
    if (provider.verified) return 'verified';
    if ((provider as any).subscription_status === 'active' || (provider as any).provider_payment_status === 'active') {
      return 'pending';
    }
    return 'inactive';
  };

  return (
    <div className="container mx-auto px-4 py-8 pb-24 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Provider Dashboard</h1>
          <p className="text-muted-foreground">Manage your business profile and view analytics</p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Status Alert */}
      <ProviderVerificationAlert status={getVerificationStatus()} />

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Profile Views</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              <p className="text-2xl font-bold">{provider.total_views || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-primary" />
              <p className="text-2xl font-bold">{provider.total_contacts || 0}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Rating</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-primary" />
              <p className="text-2xl font-bold">{provider.rating?.toFixed(1) || '0.0'}</p>
              <span className="text-sm text-muted-foreground">({provider.total_reviews || 0} reviews)</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Photos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-primary" />
              <p className="text-2xl font-bold">{provider.photos?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Business Profile */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Business Profile</CardTitle>
              <CardDescription>Your business information displayed to customers</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowBusinessInfoModal(true)}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-semibold text-lg">{provider.business_name}</h3>
            <Badge variant="outline" className="mt-1">{provider.provider_type.replace('_', ' ')}</Badge>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span>{provider.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{provider.email}</span>
            </div>
            {provider.whatsapp && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span>WhatsApp: {provider.whatsapp}</span>
              </div>
            )}
          </div>

          {provider.description && (
            <div>
              <p className="text-sm text-muted-foreground">{provider.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Location Manager */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Business Location</CardTitle>
              <CardDescription>Your primary business address</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowLocationModal(true)}
            >
              <MapPin className="w-4 h-4 mr-2" />
              Update Location
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium">{provider.address}</p>
              <p className="text-sm text-muted-foreground">
                {[provider.city, provider.state, provider.country].filter(Boolean).join(', ')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services & Schedule */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Services Offered</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowServicesModal(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {provider.services && provider.services.length > 0 ? (
                provider.services.map((service, idx) => (
                  <Badge key={idx} variant="secondary">{service}</Badge>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No services added yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Business Hours</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowHoursModal(true)}
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {provider.schedule && Object.entries(provider.schedule).map(([day, hours]: [string, any]) => {
              if (day === 'emergency_available') return null;
              return (
                <div key={day} className="flex justify-between text-sm">
                  <span className="capitalize font-medium">{day}</span>
                  <span className="text-muted-foreground">
                    {hours.closed ? 'Closed' : `${hours.open} - ${hours.close}`}
                  </span>
                </div>
              );
            })}
            
            {provider.schedule?.emergency_available && (
              <div className="pt-2 mt-2 border-t">
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Available for Emergency Services 24/7
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Photo Gallery */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Business Photos</CardTitle>
              <CardDescription>Photos displayed on your public profile ({provider.photos?.length || 0}/5)</CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPhotosModal(true)}
            >
              <Image className="w-4 h-4 mr-2" />
              Manage Photos
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {provider.photos && provider.photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {provider.photos.map((photo, idx) => (
                <div key={idx} className="relative aspect-square rounded-lg overflow-hidden">
                  <img 
                    src={photo} 
                    alt={`Business photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm mb-3">No photos uploaded yet</p>
              <Button 
                variant="outline" 
                onClick={() => setShowPhotosModal(true)}
              >
                <Image className="w-4 h-4 mr-2" />
                Upload Photos
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Portfolio / Gallery Section */}
      <ProviderGallery
        providerId={provider.id || ''}
        userId={user?.id || ''}
        isOwner={true}
        maxStorageBytes={(provider as any).max_storage_bytes || 3221225472}
        storageUsedBytes={(provider as any).storage_used_bytes || 0}
      />

      {/* Modals */}
      <UpdateProviderLocation
        open={showLocationModal}
        onOpenChange={setShowLocationModal}
        currentAddress={provider.address}
        onUpdate={handleLocationUpdate}
      />

      <EditBusinessInfoModal
        open={showBusinessInfoModal}
        onOpenChange={setShowBusinessInfoModal}
        currentData={{
          business_name: provider.business_name,
          phone: provider.phone,
          email: provider.email,
          whatsapp: provider.whatsapp,
          description: provider.description,
        }}
        onUpdate={updateProfile}
      />

      <EditServicesModal
        open={showServicesModal}
        onOpenChange={setShowServicesModal}
        currentServices={provider.services || []}
        providerType={provider.provider_type}
        onUpdate={(services) => updateProfile({ services })}
      />

      <EditBusinessHoursModal
        open={showHoursModal}
        onOpenChange={setShowHoursModal}
        currentSchedule={provider.schedule}
        onUpdate={(schedule) => updateProfile({ schedule })}
      />

      <ManagePhotosModal
        open={showPhotosModal}
        onOpenChange={setShowPhotosModal}
        currentPhotos={provider.photos || []}
        userId={user?.id || ''}
        onPhotosUpdate={(photos) => updateProfile({ photos })}
      />
    </div>
  );
}
